import { openDownloadUrl } from "util/openDownloadUrl";
import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import { FileIcon } from "components/design/FileIcon";
import Loader from "components/DesignSystem/Loader/loader";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { DeleteIcon } from "components/icons/delete";
import dayjs from "dayjs";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import {
  useLazyGetJEBulkTemplateQuery,
  useUploadBulkJournalEntryMutation,
} from "store/apis/journalEntry";
import { BackendError } from "types/utils/error";
import { useDropzone } from "react-dropzone";
import { useState } from "react";
import { FILE_UPLOAD_MESSAGE } from "constants/documents";
import DownloadSimple from "static/images/DownloadSimple.svg";
import UploadFile from "static/images/UploadFile.svg";
import CheckCircleFilled from "static/images/CheckCircleFilled.svg";

export const UploadedFileCard = ({
  selectedFile,
  onDelete,
}: {
  selectedFile: File;
  onDelete: () => void;
}) => (
  <div className="t-space-y-2">
    <div className="t-text-body-sm t-text-text-30">Uploaded file:</div>
    <div className="t-flex t-items-center t-justify-between t-p-3 t-rounded t-border t-border-solid t-border-neutral-20">
      <div className="t-flex t-gap-3 t-items-center">
        <FileIcon fileType={selectedFile.type} size="32" />
        <div className="t-space-y-1">
          <div className="t-text-subtitle-sm t-text-blue-100">
            {selectedFile.name}
          </div>
          <div className="t-text-caption t-text-neutral-60">
            {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
          </div>
        </div>
      </div>
      <Button size="small" customType="ghost" type="button" onClick={onDelete}>
        <DeleteIcon />
      </Button>
    </div>
  </div>
);

export const ImportJournalEntry = ({
  setBulkFileId,
  selectedFile,
  setSelectedFile,
}: {
  setBulkFileId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedFile: File | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | null>>;
}) => {
  const { alertToast, successToast } = useToast();
  const entityId = useCurrentEntityId();
  const { link } = useConstructInternalLink();
  const [uploadBulkJournalEntries, { isLoading: isUploading }] =
    useUploadBulkJournalEntryMutation();
  const [fetchJETemplate, { isLoading: isTemplateDownloading }] =
    useLazyGetJEBulkTemplateQuery();

  const handleTemplateDownload = async () => {
    try {
      const { download_url } = await fetchJETemplate({ entityId }).unwrap();
      openDownloadUrl(download_url);
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };
  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const onDrop = async (files: File[]) => {
    try {
      const result = await uploadBulkJournalEntries({
        file: files[0],
        entityId,
      }).unwrap();
      setSelectedFile(files[0]);
      setBulkFileId(result.bulk_file_id);
      successToast({ message: FILE_UPLOAD_MESSAGE });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const onDropRejected = () => {
    alertToast({ message: "Please upload a XLS or XLSX file exclusively." });
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject, open } =
    useDropzone({
      noClick: true,
      noKeyboard: true,
      onDrop,
      maxSize: 25000000,
      accept: {
        "application/vnd.ms-excel": [".xls"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
      },
      onDropRejected,
      multiple: false,
    });

  const deleteFile = () => {
    setSelectedFile(null);
    setBulkFileId(null);
  };

  return (
    <Modal.Body className={classNames({ "t-pb-10": !selectedFile })}>
      <div className="t-text-body-sm t-text-text-100 t-mb-3">
        Download the pre-formatted template to ensure your data is properly
        structured. Add the journal entry details to the template and upload it.
      </div>
      <div className="t-flex t-flex-col t-gap-5">
        <div>
          <Button
            size="small"
            onClick={handleTemplateDownload}
            isLoading={isTemplateDownloading}
            leftIcon={
              <img
                src={DownloadSimple}
                alt="DownloadSimple"
                className="t-mr-1"
              />
            }
          >
            <div>Download Excel template</div>
          </Button>
        </div>

        {isUploading && (
          <div className="t-h-40 t-flex t-justify-center t-items-center">
            <Loader />
          </div>
        )}

        {!isUploading && (
          <>
            {selectedFile ? (
              <div className="t-flex t-flex-col t-gap-2">
                <UploadedFileCard
                  selectedFile={selectedFile}
                  onDelete={deleteFile}
                />
                <div className="t-flex t-items-center t-gap-1">
                  <img
                    src={CheckCircleFilled}
                    alt="CheckCircleFilled"
                    className="t-size-4"
                  />
                  <div className="t-text-text-30 t-text-body-sm">
                    Upload Completed
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={classNames(
                  "t-border-2 t-rounded-lg t-border-dashed t-border-neutral-20 t-px-6 t-py-8 t-text-body-sm t-text-text-100 t-flex t-items-center t-justify-center t-flex-col t-gap-3 t-bg-surface-lighter-grey t-cursor-pointer sm:t-text-center",
                  {
                    "!t-border-green !t-text-green-70": isDragAccept,
                    "!t-border-red-50 !t-text-red-50": isDragReject,
                  }
                )}
                {...getRootProps()}
                onClick={open}
              >
                <input {...getInputProps()} id="csv" />
                <img src={UploadFile} alt="Upload File" />
                Click or drag and drop to upload the filled template
                <div className="t-text-body-sm t-text-text-30">
                  File Format: xls/xlsx
                </div>
              </div>
            )}
          </>
        )}

        {bookkeepingSettings?.accounting_lock_date && (
          <Banner
            type="neutral"
            title={
              <span>
                Your financials are locked until{" "}
                {dayjs(bookkeepingSettings.accounting_lock_date).format(
                  "MMM DD, YYYY"
                )}
              </span>
            }
            description={
              <span>
                If any journal entries are dated before this, the upload will
                fail. You can either change the journal entry dates manually,
                either update the lock date in{" "}
                <ConditionalLink
                  to={link("/books/books-settings")}
                  className="t-text-text-30 !t-underline"
                >
                  settings,
                </ConditionalLink>{" "}
                or remove journal entries from locked period and continue.
              </span>
            }
          />
        )}
      </div>
    </Modal.Body>
  );
};
